/**
 * LimitValueInline — inline-editable value for a single LIMIT/OFFSET/TOP/FETCH
 * panel row. Mirrors `AssignmentEditableValuePill` — click to enter edit mode,
 * Enter / blur commits, Escape reverts. Empty commits clear that part of the
 * clause (which may remove the whole clause when both count and offset end up
 * unset — the `modify-limit` op handles that).
 *
 * The FETCH row's display text carries the full tail ("FIRST 10 ROWS ONLY"),
 * so entering edit mode seeds the input with just the extracted number, not
 * the full text. TOP / LIMIT / OFFSET rows are plain numbers already.
 *
 * At rest the value is a bordered chip like the SELECT / ORDER BY items, with a
 * `×` that clears this part of the clause — the same empty-value `modify-limit`
 * commit an emptied input performs (analytics: the generic
 * `feature:vqe_edit_commit { op: 'modify-limit' }` fired by `commitEdit`).
 */
import { useEffect, useRef, useState } from 'react';

import { cn } from '../../../../../lib/utils';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyLimit } from '../../edit/edits/modifyLimit';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { RemoveButton } from '../common/RemoveButton';
import { TOGGLE_PILL_HOVER_AFFORDANCE } from '../common/TogglePill';
import { VqeTooltip } from '../common/VqeTooltip';
import { escapeClaimProps } from '../../../common/utils/escapeClaimTarget';

const NUMERIC_INPUT = /^\d*$/;

export interface LimitRowLike {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly role: 'count' | 'offset';
}

export interface LimitValueInlineProps {
  readonly row: LimitRowLike;
  readonly editContext: VqeEditContext;
  /** The other role's current commit value, threaded through unchanged. */
  readonly currentCount: string | undefined;
  readonly currentOffset: string | undefined;
}

function seedNumber(row: LimitRowLike): string {
  if (row.testId === 'vqe-limit-fetch') {
    return (row.value.match(/\d+/) ?? [''])[0] ?? '';
  }
  return row.value;
}

export function LimitValueInline({
  row,
  editContext,
  currentCount,
  currentOffset,
}: LimitValueInlineProps) {
  const [editing, setEditing] = useState(false);
  const seed = seedNumber(row);
  const [value, setValue] = useState(seed);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setValue(seed);
  }, [seed, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = (): void => {
    const trimmed = value.trim();
    if (trimmed === seed.trim()) {
      setEditing(false);
      setValue(seed);
      return;
    }
    setEditing(false);
    commitValue(trimmed);
  };

  /** Writes `next` for this row's role ('' clears it), keeping the other role. */
  const commitValue = (next: string): void => {
    const ctx = toCommitCtx(editContext);
    // The OTHER role's threaded current value (a pre-existing `LIMIT ALL` or
    // `TOP (n) PERCENT` clause, say) can fail `modifyLimit`'s numeric
    // validation even when this row's own input is numeric-masked. Since P12
    // §4.2 made `commitEdit` non-throwing, this `.catch` now covers ONLY
    // `modifyLimit`'s wrapper pre-validation throw (a commit-pipeline failure
    // records its own feedback toast inside commitEdit) — catch it so that
    // wrapper reject becomes a commit-feedback toast instead of reaching
    // GlobalErrorListener's crash pipeline.
    const onFailure = (error: unknown): void => {
      useVqeStore.getState().recordCommitFeedback({
        kind: 'failure',
        at: Date.now(),
        message: error instanceof Error ? error.message : String(error),
      });
    };
    if (row.role === 'count') {
      void modifyLimit({ count: next || undefined, offset: currentOffset }, ctx).catch(onFailure);
    } else {
      void modifyLimit({ count: currentCount, offset: next || undefined }, ctx).catch(onFailure);
    }
  };

  const cancel = (): void => {
    setEditing(false);
    setValue(seed);
  };

  if (!editing) {
    return (
      <span
        className="inline-flex min-w-0 items-center gap-1 rounded-md border border-border bg-muted/40 py-0.5 pl-2 pr-1"
        data-testid={`${row.testId}-chip`}
      >
        <VqeTooltip content={vqeCopy.hint.limitInlineEdit} shrink>
          <button
            type="button"
            className={cn('truncate text-left font-mono text-xs hover:underline', TOGGLE_PILL_HOVER_AFFORDANCE)}
            data-testid={`${row.testId}-value`}
            aria-label={`Edit ${row.label} value`}
            onClick={() => setEditing(true)}
          >
            {row.value || '—'}
          </button>
        </VqeTooltip>
        <RemoveButton
          testId={`${row.testId}-remove`}
          ariaLabel={`Remove ${row.label}`}
          disabled={false}
          onClick={() => commitValue('')}
        />
      </span>
    );
  }

  // The `escapeClaimProps` marker lets `CompactVqePopover`'s `onEscapeKeyDown`
  // claim Escape for THIS input while it's mounted (F1 — Escape-in-popover
  // seam consolidation) so Escape cancels the edit only, never the ancestor
  // popover; inert (and harmless) outside a compact-popover host.
  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => {
        if (NUMERIC_INPUT.test(e.target.value)) setValue(e.target.value);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      data-testid={`${row.testId}-input`}
      className="flex-1 rounded border border-input bg-background px-1.5 py-0.5 font-mono text-xs"
      aria-label={`Edit ${row.label} value`}
      {...escapeClaimProps}
    />
  );
}
